import { useScrollControl } from '@hooks/useScrollControl'
import { useScrollBarStore } from '@stores/useScrollBarStore'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Animated,
  ScrollView as ReactScrollView,
  NativeScrollEvent,
  NativeSyntheticEvent,
} from 'react-native'
import { IOScrollView } from 'react-native-intersection-observer'
import { View, isWeb, useMedia } from 'tamagui'

interface IScrollBarProps {
  children: React.ReactNode
  maxHeight: number
  displayNativeIndicator?: boolean
  scrollBarRight?: number
  scrollBarId: string
  onScroll?: (event: NativeSyntheticEvent<NativeScrollEvent>) => void
}

interface IScrollProps {
  layoutMeasurement: { height: number }
  contentOffset: { y: number }
  contentSize: { height: number }
}

export function ScrollBar({
  children,
  maxHeight,
  displayNativeIndicator = true,
  scrollBarRight = 0,
  scrollBarId,
  onScroll,
}: IScrollBarProps) {
  const [displayScrollBar, setDisplayScrollBar] = useState(false)
  const [scrollPosition, setScrollPosition] = useState(0)
  const [yPosition, setYPosition] = useState(0)
  const media = useMedia()
  const scrollViewRef = useRef<ReactScrollView>(null)
  const { registerScrollBar, unregisterScrollBar } = useScrollBarStore()
  const { isScrollingContent, setIsScrollingContent } = useScrollControl()
  const contentSizeRef = useRef<number>(0)
  const debounceTimerRef = useRef<NodeJS.Timeout>()

  const scrollTo = useCallback((y: number) => {
    scrollViewRef.current?.scrollTo({ y, animated: true })
  }, [])

  useEffect(() => {
    registerScrollBar(scrollBarId, scrollTo)

    return () => {
      unregisterScrollBar(scrollBarId)
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [scrollBarId, registerScrollBar, unregisterScrollBar, scrollTo])

  const updateScrollBarVisibility = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    debounceTimerRef.current = setTimeout(() => {
      if (contentSizeRef.current > maxHeight) {
        setDisplayScrollBar(true)
      } else {
        setDisplayScrollBar(false)
      }
    }, 100)
  }, [maxHeight])

  function calculateScroll({
    layoutMeasurement,
    contentOffset,
    contentSize,
  }: IScrollProps) {
    if (layoutMeasurement.height === 0) return

    contentSizeRef.current = contentSize.height
    const value = (layoutMeasurement.height / contentSize.height) * 100
    const yPosition =
      contentOffset.y / (contentSize.height / layoutMeasurement.height)

    setScrollPosition(value)
    setYPosition(yPosition)

    if (value >= 100) {
      setDisplayScrollBar(false)
    } else {
      updateScrollBarVisibility()
    }
  }

  function firstLoadScroll() {
    scrollTo(1)
  }

  const scrollBarMarginLeft = media.gtSm ? 14 : 10

  const handleScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    if (maxHeight < event.nativeEvent.contentSize.height)
      setDisplayScrollBar(true)
    calculateScroll(event.nativeEvent)
    onScroll?.(event)
  }

  const handleScrollStart = () => {
    if (isWeb) return
    setIsScrollingContent(true)
  }

  const handleScrollEnd = () => {
    if (isWeb) return
    setIsScrollingContent(false)
  }

  // Recalcular após mudanças potenciais de layout
  useEffect(() => {
    // Verificar tamanho após um tempo para garantir que as animações de accordion terminaram
    const timerId = setTimeout(() => {
      updateScrollBarVisibility()
    }, 300)

    return () => clearTimeout(timerId)
  }, [children, updateScrollBarVisibility])

  return (
    <View style={{ flex: 1, flexDirection: 'row' }} maxHeight={maxHeight}>
      <IOScrollView
        ref={scrollViewRef}
        showsVerticalScrollIndicator={displayNativeIndicator}
        onLayout={({
          nativeEvent: {
            layout: { height, y },
          },
        }) => {
          firstLoadScroll()
          contentSizeRef.current = height
          updateScrollBarVisibility()
          calculateScroll({
            layoutMeasurement: { height: 0 },
            contentOffset: { y },
            contentSize: { height },
          })
        }}
        onContentSizeChange={(_, height) => {
          contentSizeRef.current = height
          updateScrollBarVisibility()
        }}
        onScroll={handleScroll}
        scrollEventThrottle={16}
        nestedScrollEnabled={!isScrollingContent}
        onScrollBeginDrag={handleScrollStart}
        onScrollEndDrag={handleScrollEnd}
      >
        {children}
      </IOScrollView>
      {displayScrollBar && (
        <View
          backgroundColor="$neutral-200"
          style={{
            height: '100%',
            width: media.gtSm ? 8 : 4,
            borderRadius: 8,
            marginLeft: scrollBarMarginLeft,
            right: scrollBarRight,
            position: 'relative',
          }}
        >
          <Animated.View
            style={{
              width: media.gtSm ? 8 : 4,
              borderRadius: 8,
              backgroundColor: '#FF6485',
              height: `${scrollPosition}%`,
              transform: [{ translateY: yPosition }],
              minHeight: `${scrollPosition}%`,
              maxHeight,
            }}
          />
        </View>
      )}
    </View>
  )
}
